// =============================================================================
// MeuDriver — useTripConsent
// -----------------------------------------------------------------------------
// Consentimento do instrutor para compartilhar localização durante o
// deslocamento. Não se confunde com a permissão do sistema operacional: esta é
// a autorização dada à plataforma, revogável a qualquer momento e independente
// do que o Android/iOS concedeu.
//
// Revogar derruba qualquer rastreio em andamento (a RPC cuida disso).
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

const tripConsentKeys = {
  mine: (userId: string | undefined) => ['trip-consent', userId] as const,
}

/** `true` quando há consentimento vigente (concedido e não revogado). */
export function useTripConsent() {
  const { user } = useAuth()

  return useQuery({
    queryKey: tripConsentKeys.mine(user?.id),
    enabled: !!user?.id,
    queryFn: async (): Promise<boolean> => {
      const { data: instructor, error: instErr } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user!.id)
        .maybeSingle()

      if (instErr) throw new Error(instErr.message)
      if (!instructor) return false

      const { data, error } = await supabase
        .from('instructor_location_consent')
        .select('granted_at, revoked_at')
        .eq('instructor_id', instructor.id)
        .maybeSingle()

      if (error) throw new Error(error.message)
      return !!data?.granted_at && !data?.revoked_at
    },
  })
}

export function useSetTripConsent() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (granted: boolean) => {
      const { error } = await supabase.rpc('trip_set_consent', { p_granted: granted })
      if (error) {
        Sentry.captureException(error, { tags: { source: 'useSetTripConsent' } })
        throw new Error(error.message)
      }
      return granted
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tripConsentKeys.mine(user?.id) })
    },
  })
}
